/**
 * PDT Status Card component (T146).
 *
 * Displays the current Pattern Day Trader rule status including:
 * - Day trade count vs limit
 * - Progress bar showing usage
 * - Status indicator (safe, warning, at limit)
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { complianceService } from '@/services/compliance';
import { formatShortDate } from '@/lib/utils';

export function PDTStatusCard() {
  const {
    data: pdtStatus,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['compliance', 'pdt-status'],
    queryFn: () => complianceService.getPDTStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse h-32 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  if (error || !pdtStatus) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-sm text-muted-foreground">Unable to load PDT status</div>
        </CardContent>
      </Card>
    );
  }

  const usagePercent =
    pdtStatus.dayTradesLimit > 0 ? (pdtStatus.dayTradesCount / pdtStatus.dayTradesLimit) * 100 : 0;

  /**
   * Get status color based on day trade usage.
   * - Green: 0-1 day trades (safe)
   * - Yellow: 2 day trades (warning)
   * - Red: 3+ day trades (at limit)
   */
  const getStatusColor = () => {
    if (pdtStatus.isAtLimit) return 'text-red-500';
    if (pdtStatus.remaining <= 1) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getProgressColor = () => {
    if (pdtStatus.isAtLimit) return 'bg-red-500';
    if (pdtStatus.remaining <= 1) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const StatusIcon = pdtStatus.isAtLimit
    ? XCircle
    : pdtStatus.remaining <= 1
      ? AlertTriangle
      : CheckCircle;

  const getStatusText = () => {
    if (pdtStatus.isAtLimit) return 'At Limit';
    if (pdtStatus.remaining === 1) return 'Warning';
    return 'Safe';
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Day Trade Status</CardTitle>
        <div className={`flex items-center gap-1 text-xs ${getStatusColor()}`}>
          <StatusIcon className="h-4 w-4" />
          <span>{getStatusText()}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Day Trade Count */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>Day Trades (5-Day Rolling)</span>
            <span className={getStatusColor()}>
              {pdtStatus.dayTradesCount} / {pdtStatus.dayTradesLimit}
            </span>
          </div>
          <Progress
            value={Math.min(usagePercent, 100)}
            className="h-2"
            indicatorClassName={getProgressColor()}
          />
        </div>

        {/* Remaining Trades */}
        <div className="flex justify-between text-sm">
          <span>Remaining Today</span>
          <span className={pdtStatus.remaining === 0 ? 'text-red-500 font-medium' : ''}>
            {pdtStatus.remaining} trade{pdtStatus.remaining !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Rolling Window Period */}
        <div className="pt-2 border-t">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>
              Window: {formatShortDate(pdtStatus.windowStart)} -{' '}
              {formatShortDate(pdtStatus.windowEnd)}
            </span>
          </div>
        </div>

        {/* Warning Message */}
        {pdtStatus.isAtLimit && (
          <div className="text-xs text-red-500 bg-red-50 dark:bg-red-950 p-2 rounded">
            You have reached the day trade limit. Additional day trades may flag your account as a
            Pattern Day Trader.
          </div>
        )}

        {pdtStatus.remaining === 1 && !pdtStatus.isAtLimit && (
          <div className="text-xs text-yellow-600 dark:text-yellow-500 bg-yellow-50 dark:bg-yellow-950 p-2 rounded">
            You have 1 day trade remaining. Use it wisely.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PDTStatusCard;
